import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { unwrapResult } from '@reduxjs/toolkit';
import { toast } from 'react-toastify';
import { Tooltip } from '@mui/material';

import { errorNormalizer } from '../../../../shared/Helpers/functions';
import { CustomAlert, CustomButton, Text } from '../../../../shared/uiComponents';
import { PEARL, RED, TEXT } from '../../../../shared/Helpers/colors';
import { Trash, TrashEmpty } from '../../../../shared/Assets/sessionDetailsSVGs';
import { useResetCalendarData } from '../../Helpers/hooks';
import {
	DELETE_DESCRIPTION_SESSION,
	DELETE_DESCRIPTION_SOAP,
	DELETE_SESSION_BT_WARNING,
} from '../../../../shared/Helpers/constants';
import { AdminTypes } from '../../../../components/Menu';

import { DispatchProperties, useSelector } from '../../../../redux/store';
import { deleteEvent, deleteUserEvent } from '../../../../redux/State/clientSlice/calendarSlice';
import { SoapNotesStatuses } from '../../../../redux/API/ClientAPIHelpers/dataCollectionProperties';

const Content = ({ onClose }: { onClose: () => void }) => {
	const dispatch = useDispatch<DispatchProperties>();
	const { resetData } = useResetCalendarData();

	const data = useSelector((state) => state.calendarHelper.infoEventData);
	const { client, therapist } = useSelector((state) => state.calendarHelper.filterData);

	const deleteEventBCBAHandler = () => {
		if (!data?.id) return;
		dispatch(deleteEvent(data.id))
			.then(unwrapResult)
			.then(() => {
				toast('Deleted');
				resetData();
			})
			.catch(errorNormalizer);
	};

	const deleteEventAdminHandler = () => {
		if (!data?.id || !data.user.id) return;
		const userId = data.user.id;
		const eventId = data.id;

		dispatch(deleteUserEvent({ userId, eventId }))
			.then(unwrapResult)
			.then(() => {
				toast('Deleted');
				resetData();
			})
			.catch(errorNormalizer);
	};

	const deleteHandler = () => {
		const clientId = client?.id;
		const userId = therapist?.id;

		if (!clientId && !userId) {
			deleteEventBCBAHandler();

			return;
		}

		deleteEventAdminHandler();
	};

	return (
		<div className="deleteContainer">
			<div className="description">
				<Text title={DELETE_DESCRIPTION_SESSION} type="psm" color={TEXT.TERTIARY} />
				{data?.reportId && SoapNotesStatuses.draft !== data?.reportStatus?.status && (
					<Text title={DELETE_DESCRIPTION_SOAP} type="psm" color={TEXT.TERTIARY} />
				)}
			</div>
			<div className="deleteActions">
				<CustomButton title="Cancel" onClick={onClose} buttonType="secondaryGray" />
				<CustomButton
					title="Yes, Delete"
					onClick={deleteHandler}
					style={{
						backgroundColor: RED.BASE,
					}}
				/>
			</div>
		</div>
	);
};

const DeleteSession = () => {
	const [open, setOpen] = useState<boolean>(false);
	const data = useSelector((state) => state.calendarHelper.infoEventData);
	const role = useSelector((state) => state.account.role);

	const isBT = role?.section.id === AdminTypes.bt;
	const isSessionCompleted = data?.isCompleted;
	const shouldDisableForBT = isBT && isSessionCompleted;

	const openHandler = () => setOpen(true);
	const closeHandler = () => setOpen(false);

	return (
		<>
			<Tooltip disableHoverListener={!shouldDisableForBT} title={DELETE_SESSION_BT_WARNING}>
				<span>
					<CustomButton
						title="Delete Event"
						onClick={openHandler}
						buttonType="secondaryGray"
						startIcon={<TrashEmpty color={shouldDisableForBT ? PEARL.BASE[400] : RED.BASE} />}
						style={shouldDisableForBT ? undefined : { backgroundColor: RED.QUATERNARY, color: RED.BASE }}
						disabled={shouldDisableForBT}
					/>
				</span>
			</Tooltip>
			<CustomAlert
				open={open}
				onClose={closeHandler}
				title="Attention"
				titleOnTop={false}
				Content={Content}
				Icon={() => <Trash color={RED.BASE} />}
				iconBackgroundColor={RED.TERTIARY}
			/>
		</>
	);
};

export default DeleteSession;
